import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format, subDays, startOfQuarter, startOfYear } from 'date-fns';
import { JobCategory } from '@/lib/types/job';
import { useAuth } from '@/lib/auth';
import { DateRangeSelector } from '@/components/reports/spend/DateRangeSelector';
import { CategoryMultiSelect } from '@/components/reports/vendors/CategoryMultiSelect';
import { VendorKPICards } from '@/components/reports/vendors/VendorKPICards';
import { VendorPerformanceTable } from '@/components/reports/vendors/VendorPerformanceTable';
import { VendorDistributionChart } from '@/components/reports/vendors/VendorDistributionChart';
import { TopPerformingVendorsChart } from '@/components/reports/vendors/TopPerformingVendorsChart';
import { VendorExportButton } from '@/components/reports/vendors/VendorExportButton';
import {
  useVendorKPIs,
  useVendorPerformanceTable,
  useVendorDistribution,
  useTopPerformingVendors,
  TopVendorMetric,
} from '@/hooks/useVendorPerformance';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

export default function VendorsReportPage() {
  const { user, organization } = useAuth();
  const organizationId = organization?.id;
  
  // Default to last 90 days
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 90));
  const [selectedCategories, setSelectedCategories] = useState<JobCategory[]>([]);
  const [topVendorMetric, setTopVendorMetric] = useState<TopVendorMetric>('value');

  // Data hooks
  const kpisQuery = useVendorKPIs(startDate, endDate, organizationId, selectedCategories);
  const tableQuery = useVendorPerformanceTable(startDate, endDate, organizationId, selectedCategories);
  const distributionQuery = useVendorDistribution(startDate, endDate, organizationId, selectedCategories);
  const topVendorsQuery = useTopPerformingVendors(startDate, endDate, organizationId, selectedCategories, topVendorMetric);

  const handleDateChange = (start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Track report viewed
  useEffect(() => {
    analytics.track({
      name: ANALYTICS_EVENTS.REPORT_VIEWED,
      category: 'engagement',
      properties: {
        report_type: 'vendors',
      },
    });
  }, []);

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <Link to="/reports">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Vendor Performance</h1>
            <p className="text-muted-foreground mt-1">
              Showing vendor activity for: {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
            </p>
          </div>
        </div>
        <VendorExportButton
          data={tableQuery.data}
          isLoading={tableQuery.isLoading}
          startDate={startDate}
          endDate={endDate}
          categories={selectedCategories}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
        />
        <CategoryMultiSelect
          selectedCategories={selectedCategories}
          onChange={setSelectedCategories}
        />
      </div>

      {/* KPI Cards */}
      <VendorKPICards data={kpisQuery.data} isLoading={kpisQuery.isLoading} />

      {/* Vendor Performance Table */}
      <VendorPerformanceTable data={tableQuery.data} isLoading={tableQuery.isLoading} />

      {/* Charts Row */}
      <div className="grid gap-6 md:grid-cols-2">
        <VendorDistributionChart
          data={distributionQuery.data}
          isLoading={distributionQuery.isLoading}
        />
        <TopPerformingVendorsChart
          data={topVendorsQuery.data}
          isLoading={topVendorsQuery.isLoading}
          metric={topVendorMetric}
          onMetricChange={setTopVendorMetric}
        />
      </div>
    </div>
  );
}
